import Checkbox from 'tio-reimagined/components/checkbox';
import { fn } from '@ember/helper';
import Footer from 'tio-reimagined/components/footer';
import Modal from 'tio-reimagined/components/modal';
import { formatAsDollars } from 'tio-common/utils/format';
import { isEmpty, not, or } from 'tio-ui/utilities';
import { PhExclamationMark, PhQuestionMark } from 'ember-phosphor-icons';
import { Section } from 'tio-reimagined/components/layout';
import { sumByAttribute } from 'tio-common/utils/math';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import Table from 'tio-reimagined/components/table';
import Button from 'tio-reimagined/components/button';
import { on } from '@ember/modifier';
import type MatchPaymentModel from 'tio-data/models/match-payment';
import type MatchPlanModel from 'tio-data/models/match-plan';

type MatchSummarySignature = {
  Args: {
    qualifiedPayments: Array<MatchPaymentModel>;
    certifiedPayments: Array<MatchPaymentModel>;
    unqualifiedPayments: Array<MatchPaymentModel>;
    onCertifyPayments: (payments: Array<MatchPaymentModel>) => Promise<void>;
    matchPlan: MatchPlanModel;
  };
  Blocks: {
    default: [];
  };
  Element: HTMLElement;
};

class MatchSummaryComponent extends Component<MatchSummarySignature> {
  @tracked showExplainAlert = false;
  @tracked showCertifyPaymentsModal = false;
  @tracked loanPaymentsConfirmed = false;
  @tracked selectedPayments: Array<MatchPaymentModel> = [];
  @tracked certifyingPayments = false;

  onToggleExplainAlert = () => {
    this.showExplainAlert = !this.showExplainAlert;
  };

  onClickCertifyPayments = () => {
    this.showCertifyPaymentsModal = true;
  };

  paymentIsSelected = (payment: MatchPaymentModel): boolean => {
    return this.selectedPayments.includes(payment);
  };

  // O-O pattern won out in my head over the functional one here in my head; seems like
  // ember data wants it done this way anyway
  togglePaymentSelected = (payment: MatchPaymentModel) => {
    // copy selected payments array
    const newSelectedPayments = [...this.selectedPayments];
    // look for a payment in it
    const paymentIdx = newSelectedPayments.findIndex((current) => current === payment);
    if (paymentIdx > -1) {
      // found the payment, splice it out of the new array
      newSelectedPayments.splice(paymentIdx, 1);
    } else {
      // didn't find the payment, push it onto the new array
      newSelectedPayments.push(payment);
    }
    // uncheck confirmation if last payment was deselected
    if (isEmpty(newSelectedPayments)) this.loanPaymentsConfirmed = false;
    // set component array to the new one
    this.selectedPayments = newSelectedPayments;
  };

  openCertifyPayments = () => {
    this.showCertifyPaymentsModal = true;
  };

  closeCertifyPayments = () => {
    this.showCertifyPaymentsModal = false;
    this.selectedPayments = [];
    this.loanPaymentsConfirmed = false;
  };

  toggleLoanPaymentsConfirmed = () => {
    this.loanPaymentsConfirmed = !this.loanPaymentsConfirmed;
  };

  certifySelectedPayments = async () => {
    try {
      this.certifyingPayments = true;
      await this.args.onCertifyPayments(this.selectedPayments);
      this.closeCertifyPayments();
      this.selectedPayments = [];
      this.loanPaymentsConfirmed = false;
    } catch (e) {
      console.log(e);
    } finally {
      this.certifyingPayments = false;
    }
  };

  get deadlineValue() {
    const today = new Date();
    const thisYear = today.getFullYear();
    const urlParams = new URLSearchParams(window.location.search);
    const year = decodeURIComponent(urlParams.get('benefitYear') || '') || thisYear;
    const yearsKey = 'benefit_years';
    const dateKey = 'final_certification_date';
    return this.args.matchPlan.metadata[yearsKey][year.toString()]?.[dateKey] || '';
  }

  <template>
    <Section>
      <:header>
        {{! TODO: shouldn't need to inject this class into a meaningless div, should
        have a way for the GenericCard to handle this if these are the actual styles we
        want the title section to have }}
        <div class="mb-4 text-2xl">
          {{t "syf.dashboard.match.summary.title"}}
        </div>
      </:header>
      <:body>
        <dl class="mb-3 inline-block">
          <div class="flex flex-row">
            <dt class="mr-6">{{t "syf.dashboard.match.summary.deadline_label"}}</dt>
            {{! @glint-expect-error: date triggers this }}
            <dd class="mr-6 font-semibold">{{this.deadlineValue}}</dd>
          </div>
        </dl>
        {{#unless (isEmpty @qualifiedPayments)}}
          <Button
            @appearance="outlined"
            @intent="danger"
            @icon={{PhExclamationMark}}
            @iconPlacement="start"
            {{on "click" this.openCertifyPayments}}
            class="inline-block"
          >
            {{t "syf.dashboard.match.summary.certify_link_text"}}
          </Button>
        {{/unless}}
        <Table @isLoading={{false}}>
          <:thead as |options|>
            <options.tr>
              <options.th />
              <options.th>{{t "syf.dashboard.match.summary.table.count_header"}}</options.th>
              <options.th>{{t "syf.dashboard.match.summary.table.amount_header"}}</options.th>
            </options.tr>
          </:thead>
          <:tbody as |options|>
            <options.tr>
              <options.td>{{t "syf.dashboard.match.summary.table.certified_label"}}</options.td>
              <options.td>{{@certifiedPayments.length}}</options.td>
              <options.td>{{formatAsDollars
                  (sumByAttribute @certifiedPayments "transactionAmount")
                }}</options.td>
            </options.tr>
            <options.tr>
              <options.td>{{t "syf.dashboard.match.summary.table.qualified_label"}}</options.td>
              <options.td>{{@qualifiedPayments.length}}</options.td>
              <options.td>{{formatAsDollars
                  (sumByAttribute @qualifiedPayments "transactionAmount")
                }}</options.td>
            </options.tr>
            <options.tr>
              <options.td>{{t "syf.dashboard.match.summary.table.unqualified_label"}}</options.td>
              <options.td>{{@unqualifiedPayments.length}}</options.td>
              <options.td>{{formatAsDollars
                  (sumByAttribute @unqualifiedPayments "transactionAmount")
                }}</options.td>
            </options.tr>
          </:tbody>
        </Table>
        <Button
          @appearance="minimal"
          @intent="primary"
          @icon={{PhQuestionMark}}
          @iconPlacement="start"
          {{on "click" this.onToggleExplainAlert}}
          class="text-sm"
        >
          {{t "syf.dashboard.match.summary.explanation.label"}}
        </Button>
      </:body>
    </Section>
    <Modal
      @isOpen={{this.showCertifyPaymentsModal}}
      @onClose={{this.closeCertifyPayments}}
      @size="xl"
    >
      <:header>{{t "syf.dashboard.match.summary.certify_payments.header"}}</:header>
      <:body>
        <p>{{t "syf.dashboard.match.summary.certify_payments.subheader"}}</p>
        <table class="w-full mb-6 text-lg text-tio-gray-700">
          <thead class="bg-ocean-50">
            <tr class="py-3">
              <th class="px-1 py-1">
                {{t "syf.dashboard.match.summary.certify_payments.table.select"}}
              </th>
              <th class="px-1 py-1">
                {{t "syf.dashboard.match.summary.certify_payments.table.servicer"}}
              </th>
              <th class="px-1 py-1">
                {{t "syf.dashboard.match.summary.certify_payments.table.date"}}
              </th>
              <th class="px-1 py-1">
                {{t "syf.dashboard.match.summary.certify_payments.table.amount"}}
              </th>
            </tr>
          </thead>
          <tbody>
            {{#each @qualifiedPayments as |payment|}}
              <tr class="py-2">
                <td class="py-2 text-center">
                  <Checkbox
                    data-test-id="payment-selected-checkbox"
                    @onChange={{fn this.togglePaymentSelected payment}}
                    @checked={{this.paymentIsSelected payment}}
                    class="cursor-pointer inline-block"
                  />
                </td>
                <td
                  class="py-2 text-center"
                >{{payment.accountTransaction.financialInstitution.name}}</td>
                <td class="py-2 text-center">{{payment.transactionDate}}</td>
                <td class="py-2 text-center">{{formatAsDollars payment.transactionAmount}}</td>
              </tr>
            {{/each}}
          </tbody>
        </table>
        <Checkbox
          data-test-id="certify-payments-checkbox"
          @onChange={{this.toggleLoanPaymentsConfirmed}}
          @checked={{this.loanPaymentsConfirmed}}
          @isDisabled={{isEmpty this.selectedPayments}}
          class="cursor-pointer"
          @label={{t "syf.dashboard.match.summary.certify_payments.statement"}}
        />
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.certifyingPayments}}
          @onSubmit={{this.certifySelectedPayments}}
          @submitText="Certify"
          @onCancel={{this.closeCertifyPayments}}
          @submitDisabled={{or (not this.loanPaymentsConfirmed) this.certifyingPayments}}
        />
      </:footer>
    </Modal>
    <Modal @isOpen={{this.showExplainAlert}} @onClose={{this.onToggleExplainAlert}}>
      <:header>{{t "syf.dashboard.match.summary.explanation.label"}}</:header>
      <:body>
        <h3 class="mb-2 text-base text-left font-semibold text-tio-gray-700">
          {{t "syf.dashboard.match.summary.explanation.section.one.title"}}
        </h3>
        <p class="mb-4 text-left text-tio-gray-700">
          {{t "syf.dashboard.match.summary.explanation.section.one.content"}}
        </p>
        <h3 class="mb-2 text-base text-left font-semibold text-tio-gray-700">
          {{t "syf.dashboard.match.summary.explanation.section.two.title"}}
        </h3>
        <p class="mb-4 text-left text-tio-gray-700">
          {{t "syf.dashboard.match.summary.explanation.section.two.content"}}
        </p>
        <h3 class="mb-2 text-base text-left font-semibold text-tio-gray-700">
          {{t "syf.dashboard.match.summary.explanation.section.three.title"}}
        </h3>
        <p class="mb-4 text-left text-tio-gray-700">
          {{t "syf.dashboard.match.summary.explanation.section.three.content"}}
        </p>
      </:body>
      <:footer>
        <Footer @onSubmit={{this.onToggleExplainAlert}} @submitText={{t "close"}} />
      </:footer>
    </Modal>
  </template>
}

export default MatchSummaryComponent;
